import { useMemo, useState } from "react";
import { niceTimeScale, selectTickIndexes, trendPoints } from "./trendSeries";

const COLORS = {
  active: "#2b6df2",
  auto: "#7c3aed",
  pause: "#f59e0b",
};

export default function ActivityOverviewChart({ days = [], sessions = [], from, to }) {
  const [hover, setHover] = useState(null);
  const series = useMemo(
    () => trendPoints({ from, to, days, sessions }),
    [days, from, sessions, to],
  );
  const hourly = Boolean(from && to && from === to);
  const chart = useMemo(() => buildChart(series), [series]);
  const xTicks = useMemo(
    () => selectTickIndexes(chart.points.length, 7).map((index) => chart.points[index]),
    [chart.points],
  );
  const showIdleDots = chart.points.length > 1 && chart.points.length <= 8;

  const onMove = (event) => {
    if (!chart.points.length) return;
    const svg = event.currentTarget;
    const rect = svg.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * chart.width;
    let nearest = chart.points[0];
    let best = Math.abs(x - nearest.x);
    for (const point of chart.points) {
      const distance = Math.abs(x - point.x);
      if (distance < best) {
        nearest = point;
        best = distance;
      }
    }
    setHover(nearest);
  };

  return (
    <div className="pa-chart-wrap">
      <div className="pa-chart-body">
        <div className="pa-chart-y" aria-hidden="true">
          {chart.grid.map((line) => (
            <span key={line.label} style={{ top: `${(line.y / chart.height) * 100}%` }}>
              {line.label}
            </span>
          ))}
        </div>
        <div className="pa-chart-plot">
          <svg
            className="pa-chart"
            viewBox={`0 0 ${chart.width} ${chart.height}`}
            preserveAspectRatio="none"
            role="img"
            aria-label={
              hourly
                ? "Active time, manual pauses, and auto-pauses by hour"
                : "Active time, manual pauses, and auto-pauses by day"
            }
            onMouseMove={onMove}
            onMouseLeave={() => setHover(null)}
          >
            <defs>
              <linearGradient id="pa-active-fill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={COLORS.active} stopOpacity="0.22" />
                <stop offset="100%" stopColor={COLORS.active} stopOpacity="0" />
              </linearGradient>
            </defs>
            {chart.grid.map((line) => (
              <line
                key={line.y}
                x1={chart.pad.left}
                x2={chart.width - chart.pad.right}
                y1={line.y}
                y2={line.y}
                stroke="#e2e8f0"
                strokeDasharray="3 5"
                vectorEffect="non-scaling-stroke"
              />
            ))}
            {chart.areaPath ? <path d={chart.areaPath} fill="url(#pa-active-fill)" /> : null}
            <path
              d={chart.activePath}
              fill="none"
              stroke={COLORS.active}
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
            <path
              d={chart.autoPath}
              fill="none"
              stroke={COLORS.auto}
              strokeWidth="2"
              strokeDasharray="6 5"
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
            <path
              d={chart.pausePath}
              fill="none"
              stroke={COLORS.pause}
              strokeWidth="2"
              strokeDasharray="6 5"
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
            {showIdleDots
              ? chart.points.map((point) => (
                  <g key={`${point.key}-dots`}>
                    <circle cx={point.x} cy={point.activeY} r="3.5" fill={COLORS.active} />
                    <circle cx={point.x} cy={point.autoY} r="3" fill={COLORS.auto} />
                    <circle cx={point.x} cy={point.pauseY} r="3" fill={COLORS.pause} />
                  </g>
                ))
              : null}
            {hover ? (
              <g>
                <line
                  x1={hover.x}
                  x2={hover.x}
                  y1={chart.pad.top}
                  y2={chart.height - chart.pad.bottom}
                  stroke="#cbd5e1"
                  vectorEffect="non-scaling-stroke"
                />
                <circle cx={hover.x} cy={hover.activeY} r="5.5" fill={COLORS.active} stroke="#ffffff" strokeWidth="2" />
                <circle cx={hover.x} cy={hover.autoY} r="5" fill={COLORS.auto} stroke="#ffffff" strokeWidth="2" />
                <circle cx={hover.x} cy={hover.pauseY} r="5" fill={COLORS.pause} stroke="#ffffff" strokeWidth="2" />
              </g>
            ) : null}
          </svg>
          {hover ? (
            <div
              className="pa-tooltip"
              style={{ left: `${(hover.x / chart.width) * 100}%`, top: 12, transform: tooltipShift(hover.x, chart.width) }}
            >
              <strong>{hover.label}</strong>
              <p style={{ color: COLORS.active }}>
                <i className="pa-dot" style={{ background: COLORS.active }} />
                Active : {formatHourValue(hover.activeSeconds)}
              </p>
              <p style={{ color: COLORS.auto }}>
                <i className="pa-dot" style={{ background: COLORS.auto }} />
                Auto-paused : {formatHourValue(hover.autoPauseSeconds)}
              </p>
              <p style={{ color: COLORS.pause }}>
                <i className="pa-dot" style={{ background: COLORS.pause }} />
                Manual pause : {formatHourValue(hover.manualPauseSeconds)}
              </p>
            </div>
          ) : null}
        </div>
      </div>
      <div className="pa-chart-x" aria-hidden="true">
        {xTicks.map((point, index) => (
          <span key={point.key} style={xTickStyle(point.x, chart.width, index, xTicks.length)}>
            {point.label}
          </span>
        ))}
      </div>
      <div className="pa-legend">
        <span><i className="pa-dot" style={{ background: COLORS.active }} /> Active</span>
        <span><i className="pa-dot" style={{ background: COLORS.auto }} /> Auto-paused</span>
        <span><i className="pa-dot" style={{ background: COLORS.pause }} /> Manual pause</span>
      </div>
    </div>
  );
}

function xTickStyle(x, width, index, length) {
  const left = `${(x / width) * 100}%`;
  if (index === 0) return { left, transform: "translateX(0)" };
  if (index === length - 1) return { left, transform: "translateX(-100%)" };
  return { left, transform: "translateX(-50%)" };
}

function tooltipShift(x, width) {
  if (x / width < 0.18) return "translateX(8px)";
  if (x / width > 0.82) return "translateX(calc(-100% - 8px))";
  return "translateX(-50%)";
}

function formatHourValue(totalSeconds) {
  const hours = Math.round((Math.max(0, Number(totalSeconds) || 0) / 3600) * 10) / 10;
  return Number.isInteger(hours) ? `${hours}h` : `${hours.toFixed(1)}h`;
}

function formatTickHours(hours) {
  const rounded = Math.round(hours * 10) / 10;
  return Number.isInteger(rounded) ? `${rounded}h` : `${rounded.toFixed(1)}h`;
}

function buildChart(series) {
  const width = 760;
  const height = 240;
  const pad = { top: 10, right: 8, bottom: 10, left: 4 };
  const innerW = width - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;
  const dataMax = Math.max(
    0,
    ...series.map((item) =>
      Math.max(item.activeSeconds || 0, item.manualPauseSeconds || 0, item.autoPauseSeconds || 0),
    ),
  );
  const scale = niceTimeScale(dataMax);
  const yFor = (seconds) => pad.top + innerH - ((seconds || 0) / scale.maxSeconds) * innerH;
  const slot = series.length > 1 ? innerW / (series.length - 1) : innerW;

  const grid = scale.tickHours.map((hours) => ({
    y: yFor(hours * 3600),
    label: formatTickHours(hours),
  }));

  const points = series.map((item, index) => {
    const x = series.length === 1 ? pad.left + innerW / 2 : pad.left + slot * index;
    return {
      ...item,
      x,
      activeY: yFor(item.activeSeconds),
      autoY: yFor(item.autoPauseSeconds),
      pauseY: yFor(item.manualPauseSeconds),
    };
  });

  const activePts = points.map((point) => ({ x: point.x, y: point.activeY }));
  const autoPts = points.map((point) => ({ x: point.x, y: point.autoY }));
  const pausePts = points.map((point) => ({ x: point.x, y: point.pauseY }));
  const baseline = pad.top + innerH;

  return {
    width,
    height,
    pad,
    grid,
    points,
    activePath: splinePath(activePts, pad.top, baseline),
    autoPath: splinePath(autoPts, pad.top, baseline),
    pausePath: splinePath(pausePts, pad.top, baseline),
    areaPath: areaPath(activePts, baseline, pad.top),
  };
}

function splinePath(points, minY, maxY) {
  if (!points.length) return "";
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;
  if (points.length === 2) {
    return `M ${points[0].x} ${points[0].y} C ${mid(points[0].x, points[1].x)} ${points[0].y}, ${mid(
      points[0].x,
      points[1].x,
    )} ${points[1].y}, ${points[1].x} ${points[1].y}`;
  }

  const clampY = (value) => Math.min(maxY, Math.max(minY, value));
  const tension = 1 / 3;
  let path = `M ${points[0].x} ${points[0].y}`;
  for (let index = 0; index < points.length - 1; index += 1) {
    const p0 = points[index - 1] || points[index];
    const p1 = points[index];
    const p2 = points[index + 1];
    const p3 = points[index + 2] || p2;
    path += ` C ${p1.x + (p2.x - p0.x) * tension} ${clampY(p1.y + (p2.y - p0.y) * tension)}, ${
      p2.x - (p3.x - p1.x) * tension
    } ${clampY(p2.y - (p3.y - p1.y) * tension)}, ${p2.x} ${p2.y}`;
  }
  return path;
}

function mid(a, b) {
  return (a + b) / 2;
}

function areaPath(points, baseline, minY) {
  const line = splinePath(points, minY, baseline);
  if (!line || !points.length) return "";
  const first = points[0];
  const last = points[points.length - 1];
  return `${line} L ${last.x} ${baseline} L ${first.x} ${baseline} Z`;
}
